/**
 * Version gate — forces a hard reload when the running bundle is older than
 * what the server is currently serving. Closes the gap that Ctrl+Shift+R
 * doesn't always close (extensions, persistent client state, an old service
 * worker that may have been registered by a past deploy).
 *
 * Compares __APP_VERSION__ (baked into this bundle at build time via Vite's
 * `define`) against /version.json (served fresh, no-cache). On mismatch:
 *   1. clears Cache Storage entries (defensive — covers a future SW that
 *      might cache requests),
 *   2. attempts to unregister any active service workers,
 *   3. sets a sessionStorage marker so we don't reload again for this same
 *      target version (anti-loop guard — if the server still reports the new
 *      version after our reload, we've done what we can; nagging the user
 *      with infinite reloads doesn't help),
 *   4. hard-reloads via window.location.reload().
 *
 * Called from two places:
 *   - app boot (main.tsx) — covers tabs that load after a deploy but
 *     somehow received stale JS from a proxy / extension / disk cache,
 *   - post-login (Login.tsx) — natural latency point where a brief reload
 *     blip is acceptable, and gives users a reliable way to recover from
 *     stuck-stale state by signing out and back in.
 *
 * Failure modes are silent — a missing /version.json or a network error
 * never blocks the user's session. The gate is opportunistic, not load-
 * bearing.
 */

const VERSION_URL = '/version.json'
// One marker per target version → if the user really IS on that version
// after a reload, we won't loop. If a NEWER version ships afterward, the
// marker key changes and the gate will fire again.
const RELOAD_MARKER_PREFIX = 'jad:versionGate:reloadedFor:'

interface VersionPayload {
  version: string
  builtAt?: string
  gitSha?: string
}

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const res = await fetch(VERSION_URL, { cache: 'no-store' })
    if (!res.ok) return null
    const data = (await res.json()) as VersionPayload
    return typeof data?.version === 'string' ? data.version : null
  } catch {
    return null
  }
}

async function purgeCachesAndServiceWorkers(): Promise<void> {
  // Cache Storage — currently unused by this app, but a past or extension-
  // installed SW might have populated it. Best-effort delete.
  try {
    if (typeof caches !== 'undefined' && caches.keys) {
      const keys = await caches.keys()
      await Promise.all(keys.map(k => caches.delete(k).catch(() => false)))
    }
  } catch { /* ignore */ }

  // Unregister any service worker controlling this origin. If a deploy from
  // months ago registered one and it's still alive, this is the only way to
  // shake it loose without DevTools. Reload after this is what actually
  // takes effect (the page is still controlled by the old SW until then).
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map(r => r.unregister().catch(() => false)))
    }
  } catch { /* ignore */ }
}

/**
 * Run a version check. If the deployed version differs from the running
 * one and we haven't already reloaded for that target version this session,
 * purge caches/SWs and hard-reload. Returns true if a reload was triggered
 * (the page won't actually return — caller can still rely on it for
 * branching, e.g. delaying navigation in Login until after the gate).
 */
export async function ensureCurrentVersion(): Promise<boolean> {
  // SSR / unit-test guard
  if (typeof window === 'undefined') return false

  const running = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : ''
  if (!running) return false

  const deployed = await fetchDeployedVersion()
  if (!deployed || deployed === running) return false

  const markerKey = RELOAD_MARKER_PREFIX + deployed
  if (window.sessionStorage.getItem(markerKey) === '1') {
    // We already reloaded once aiming at this target. Either the new bundle
    // genuinely IS this version (check failed for a different reason) or
    // some upstream cache is wedged. Don't loop the user.
    return false
  }

  // eslint-disable-next-line no-console
  console.info(`[versionGate] running=${running} deployed=${deployed} — purging caches and reloading`)
  await purgeCachesAndServiceWorkers()
  try { window.sessionStorage.setItem(markerKey, '1') } catch { /* private mode */ }
  // location.reload() with cache bypass equivalent — most modern browsers
  // honor the implicit reload semantics for HTML; assets still re-fetch
  // because the new index.html will reference new content-hashed names.
  window.location.reload()
  return true
}
